import {
  ActionReducerMapBuilder,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import i18next from 'i18next';
import { ErrorResponse, HttpStatusCode } from '../api';
import { uploadVideos } from '../common/actions';
import {
  createComment,
  createPost,
  createPostInMultipleRooms,
  createPrivateRoom,
  createReplyToComment,
  deleteComment,
  deletePoll,
  deletePost,
  joinRoom,
  leaveRoom,
  moderatorDeleteComment,
  moderatorDeletePoll,
  moderatorDeletePost,
  moderatorEditComment,
  moderatorPinPost,
  moderatorRemoveMemberFromRoom,
  reportComment,
  reportPost,
  updateComment,
  updatePost,
} from '../community';
import {
  createNewPoll,
  reportPoll,
  submitPoll,
  updatePoll,
} from '../community/polls/actions';
import {
  acceptCompanionRequest,
  rejectCompanionRequest,
  removeCompanion,
  sendCompanionRequest,
} from '../companions';
import { sendFeedback } from '../feedback';
import {
  createConsultation,
  createJournal,
  deleteConsultation,
  deleteHealthJourney,
  deleteHistoricalHealthPicture,
  deleteJournal,
  saveToMyJourney,
  updateConsultation,
  updateHealthPicture,
  updateJournal,
} from '../healthJourney';
import { insertSymptoms } from '../healthJourney/symptom';
import {
  blockMember,
  Country,
  fetchCountries,
  reportMember,
  resendVerificationEmailLink,
  sendEmailsInvite,
  updateMember,
  updateMemberbership,
  updateUser,
} from '../profile';
import {
  deleteCommentOnStory,
  insertCommentOnStory,
  insertReplyToCommentOnStory,
  moderatorDeleteCommentOnStory,
  moderatorUpdateCommentOnStory,
  reportStoryComment,
  updateCommentOnStory,
} from '../stories/comments';
import {
  deleteFeaturedStory,
  deleteStory,
  insertFeaturedStory,
  insertStory,
  moderatorDeleteStory,
  reportStory,
  shareStoryToRooms,
  updateFeaturedStory,
} from '../stories/stories';
import { UploadVideoResult } from './model';
import { ServerDirective } from '../types/signlaRTypes';
import { Snackbar } from './model/Snackbar';
import { SnackbarType } from './model/SnackbarType';

export interface AppState {
  isOffline: boolean;
  snackbarOpen: boolean;
  snackbarType?: SnackbarType;
  snackbarMessage?: string;
  countries?: Country[];
  language?: string;
  bottomNavigationOpen: boolean;
  isLoggedIn?: boolean;
  isAuthInProgress?: boolean;
  openUnverifiedMemberDialog: boolean;
  resendVerificationEmailLinkCompleted?: boolean;
  resendVerificationEmailLinkLoading?: boolean;
  uploadingVideo?: boolean;
  uploadVideoResult?: UploadVideoResult;
  shouldOpenImagePicker?: boolean;
  serverDirective?: ServerDirective;
}

const initialState: AppState = {
  isOffline: false,
  snackbarOpen: false,
  snackbarType: undefined,
  snackbarMessage: undefined,
  countries: undefined,
  bottomNavigationOpen: true,
  language: '',
  isLoggedIn: false,
  isAuthInProgress: undefined,
  openUnverifiedMemberDialog: false,
  resendVerificationEmailLinkCompleted: undefined,
  resendVerificationEmailLinkLoading: undefined,
  uploadingVideo: false,
  uploadVideoResult: { error: '', video: undefined },
};

const translate = (translateKey: string) => i18next.t(translateKey);

const checkIfMemberIsUnverified = (
  action: PayloadAction<ErrorResponse | undefined>
) => {
  if (action.payload && action.payload.status === 403) {
    if (
      action.payload?.data &&
      action.payload?.data?.['error_сode'] === 'MEMBER_UNVERIFIED'
    ) {
      return true;
    }
  }
  return false;
};

const checkIfContentIsInappropriate = (
  action: PayloadAction<ErrorResponse | undefined>
) => {
  return action.payload?.status === HttpStatusCode.not_acceptable
    ? true
    : false;
};

const reportReducers = (builder: ActionReducerMapBuilder<AppState>) => {
  builder
    .addCase(reportPost.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportPost.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('snackbar_generic_api_fail');
      }
    })
    .addCase(reportComment.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportComment.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('snackbar_generic_api_fail');
      }
    })
    .addCase(reportMember.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportMember.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('snackbar_generic_api_fail');
      }
    })
    .addCase(reportStory.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportStory.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('snackbar_generic_api_fail');
      }
    })
    .addCase(reportStoryComment.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportStoryComment.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('report_toast_message');
      }
    })
    .addCase(reportPoll.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('report_toast_message');
    })
    .addCase(reportPoll.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('report_poll_toast_error_message');
      }
    })
    .addCase(submitPoll.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('submit_poll_toast_error_message');
      }
    })
    .addCase(moderatorDeleteStory.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('moderator_delete_story_success');
    })
    .addCase(moderatorDeleteStory.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('moderator_delete_story_failure');
      }
    })

    .addCase(deleteStory.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('delete_story_success');
    })
    .addCase(deleteStory.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('delete_story_failure');
      }
    })

    .addCase(moderatorDeleteCommentOnStory.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate(
        'moderator_delete_comment_story_success'
      );
    })
    .addCase(moderatorDeleteCommentOnStory.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate(
          'moderator_delete_comment_story_failure'
        );
      }
    })

    .addCase(deleteCommentOnStory.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('delete_comment_story_success');
    })
    .addCase(deleteCommentOnStory.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('delete_comment_story_failure');
      }
    })

    .addCase(updateHealthPicture.fulfilled, (state) => {
      state.snackbarOpen = true;
      state.snackbarType = SnackbarType.success;
      state.snackbarMessage = translate('health_picture_modified_success');
    })
    .addCase(updateHealthPicture.rejected, (state, action) => {
      if (checkIfMemberIsUnverified(action)) {
        state.openUnverifiedMemberDialog = true;
      } else {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = translate('health_picture_modified_error');
      }
    });
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setOffline: (state, action: PayloadAction<boolean>) => {
      state.isOffline = action.payload;
    },
    setIsVideoUploading: (state, action: PayloadAction<boolean>) => {
      state.uploadingVideo = action.payload;
    },
    deleteVideoResult: (state) => {
      state.uploadVideoResult = {
        error: '',
        video: undefined,
        originalFilename: '',
      };
    },
    setLanguage: (state, action: PayloadAction<string>) => {
      state.language = action.payload;
    },
    showSnackbar: {
      reducer(state, { payload }: PayloadAction<Snackbar>) {
        state.snackbarOpen = true;
        state.snackbarMessage = payload.message;
        state.snackbarType = payload.type;
      },
      prepare(type: SnackbarType, message: string) {
        return { payload: { type, message } };
      },
    },
    hideSnackbar: (state) => {
      state.snackbarOpen = false;
    },
    showBottomNavigation: (state) => {
      state.bottomNavigationOpen = true;
    },
    hideBottomNavigation: (state) => {
      state.bottomNavigationOpen = false;
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setIsAuthInProgress: (state, action: PayloadAction<boolean>) => {
      state.isAuthInProgress = action.payload;
    },
    setOpenUnverifiedMemberDialog: (state, action: PayloadAction<boolean>) => {
      state.openUnverifiedMemberDialog = action.payload;
    },
    setShouldOpenImagePicker: (state, action: PayloadAction<boolean>) => {
      state.shouldOpenImagePicker = action.payload;
    },
    setServerDirective: (state, action: PayloadAction<ServerDirective>) => {
      state.serverDirective = action.payload;
    },
    clearServerDirective: (state) => {
      state.serverDirective = undefined;
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAppAsyncThunk or in other slices.
  extraReducers: (builder) => {
    // Separate report reducers from others
    // maybe should be done also for the others here. TO BE Reviewed
    reportReducers(builder);

    builder
      .addCase(updateMember.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          if (action.payload?.status !== HttpStatusCode.conflict) {
            state.snackbarMessage = checkIfContentIsInappropriate(action)
              ? action.payload?.data?.['exception_message']
              : translate('snackbar_update_api_fail');
            state.snackbarOpen = true;
            state.snackbarType = SnackbarType.error;
          }
        }
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(updateMemberbership.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_update_api_fail');
        }
      })
      .addCase(updateUser.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('snackbar_profile_updated_success');
      })
      .addCase(updateUser.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_update_api_fail');
        }
      })
      .addCase(insertSymptoms.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('snackbar_add_symptom_api_success');
      })
      .addCase(insertSymptoms.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_add_symptom_api_error');
        }
      })
      .addCase(createConsultation.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_add_consultation_api_success'
        );
      })
      .addCase(createConsultation.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_add_consultation_api_error'
          );
        }
      })
      .addCase(updateConsultation.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_update_consultation_api_success'
        );
      })
      .addCase(updateConsultation.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.success;
          state.snackbarMessage = translate(
            'snackbar_update_consultation_api_error'
          );
        }
      })
      .addCase(createPost.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_add_post_success'
        );
      })
      .addCase(createPostInMultipleRooms.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_add_post_success'
        );
      })
      .addCase(createPost.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('snackbar_community_add_post_fail');
        }
      })
      .addCase(updatePost.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_update_post_success'
        );
      })
      .addCase(updatePost.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('snackbar_community_add_post_fail');
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
        }
      })
      .addCase(deletePost.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_delete_post_success'
        );
      })
      .addCase(createComment.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_add_comment_success'
        );
      })
      .addCase(createComment.rejected, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = checkIfContentIsInappropriate(action)
          ? action.payload?.data?.['exception_message']
          : translate('snackbar_community_add_comment_failure');
      })
      .addCase(createReplyToComment.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_add_reply_success'
        );
      })
      .addCase(createReplyToComment.rejected, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = checkIfContentIsInappropriate(action)
          ? action.payload?.data?.['exception_message']
          : translate('snackbar_community_add_reply_failure');
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          action.meta.arg.commentId
            ? 'snackbar_community_delete_reply_success'
            : 'snackbar_community_delete_comment_success'
        );
      })
      .addCase(deleteComment.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            action.meta.arg.commentId
              ? 'snackbar_community_delete_reply_failure'
              : 'snackbar_community_delete_comment_failure'
          );
        }
      })
      .addCase(moderatorDeleteComment.fulfilled, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          action.meta.arg.parentCommentId
            ? 'snackbar_community_delete_reply_success'
            : 'snackbar_community_delete_comment_success'
        );
      })
      .addCase(moderatorDeleteComment.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            action.meta.arg.parentCommentId
              ? 'snackbar_community_delete_reply_failure'
              : 'snackbar_community_delete_comment_failure'
          );
        }
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          action.meta.arg.commentId
            ? 'snackbar_community_update_reply_success'
            : 'snackbar_community_update_comment_success'
        );
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = checkIfContentIsInappropriate(action)
          ? action.payload?.data?.['exception_message']
          : translate('snackbar_community_update_comment_failure');
      })
      .addCase(moderatorEditComment.fulfilled, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          action.meta.arg.commentId
            ? 'snackbar_community_update_reply_success'
            : 'snackbar_community_update_comment_success'
        );
      })
      .addCase(moderatorEditComment.rejected, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        state.snackbarMessage = checkIfContentIsInappropriate(action)
          ? action.payload?.data?.['exception_message']
          : translate('snackbar_community_update_comment_failure');
      })
      .addCase(joinRoom.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_join_room_success'
        );
      })
      .addCase(leaveRoom.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_leave_room_success'
        );
      })
      .addCase(sendCompanionRequest.fulfilled, (state, action) => {
        if (!action.meta.arg.isSilentAdd) {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.success;
          state.snackbarMessage = translate(
            'companions_send_request_successfully'
          );
        }
      })
      .addCase(removeCompanion.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'companions_remove_request_successfully'
        );
      })
      .addCase(acceptCompanionRequest.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'companions_accept_request_successfully'
        );
      })
      .addCase(acceptCompanionRequest.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_generic_api_fail');
        }
      })
      .addCase(rejectCompanionRequest.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'companions_reject_request_successfully'
        );
      })
      .addCase(rejectCompanionRequest.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_generic_api_fail');
        }
      })
      .addCase(blockMember.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('companions_block_successfully');
      })
      .addCase(deleteConsultation.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_consultation_delete_success'
        );
      })
      .addCase(deleteConsultation.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_consultation_delete_fail'
          );
        }
      })
      .addCase(deleteJournal.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('snackbar_journal_delete_success');
      })
      .addCase(deleteJournal.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_journal_delete_fail');
        }
      })
      .addCase(createJournal.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('snackbar_journal_create_success');
      })
      .addCase(createJournal.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_journal_create_fail');
        }
      })
      .addCase(updateJournal.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('snackbar_journal_update_success');
      })
      .addCase(updateJournal.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('snackbar_journal_update_fail');
        }
      })
      .addCase(sendFeedback.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('feedback_success_toast_message');
      })
      .addCase(sendFeedback.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('feedback_fail_toast_message');
        }
      })
      .addCase(moderatorDeletePost.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_delete_post_success'
        );
      })
      .addCase(moderatorDeletePost.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_community_delete_post_failure'
          );
        }
      })

      .addCase(moderatorRemoveMemberFromRoom.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_moderator_remove_member_success'
        );
      })
      .addCase(moderatorRemoveMemberFromRoom.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_community_moderator_remove_member_failure'
          );
        }
      })

      .addCase(joinRoom.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_community_cannot_join_room'
          );
        }
      })
      .addCase(leaveRoom.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_community_cannot_leave_room'
          );
        }
      })

      .addCase(moderatorPinPost.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_pin_post_success'
        );
      })
      .addCase(moderatorPinPost.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage =
            action.payload && action.payload.status === 403
              ? action.payload?.data?.['exception_message']
              : translate('snackbar_community_pin_post_failure');
        }
      })
      .addCase(sendEmailsInvite.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'snackbar_profile_invites_post_fail'
          );
        }
      })
      .addCase(createNewPoll.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('create_new_poll_error');
        }
      })
      .addCase(createNewPoll.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('create_new_poll_success');
      })
      .addCase(updatePoll.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('create_new_poll_error');
        }
      })
      .addCase(moderatorDeletePoll.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('moderator_delete_poll_error');
        }
      })
      .addCase(moderatorDeletePoll.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('moderator_delete_poll_success');
      })
      .addCase(deletePoll.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('delete_poll_error');
        }
      })
      .addCase(deletePoll.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('delete_poll_success');
      })
      .addCase(shareStoryToRooms.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('share_story_to_rooms_success');
      })
      .addCase(deleteHealthJourney.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('delete_health_journey_error');
        }
      })
      .addCase(deleteHealthJourney.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('delete_health_journey_success');
      })
      .addCase(saveToMyJourney.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('health_picture_isabel_save_error');
        }
      })
      .addCase(saveToMyJourney.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('health_picture_isabel_save_success');
      })
      .addCase(deleteHistoricalHealthPicture.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'delete_historical_health_picture_message_success'
        );
      })
      .addCase(deleteHistoricalHealthPicture.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate(
            'delete_historical_health_picture_message_error'
          );
        }
      })
      .addCase(insertStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_story_error');
        }
      })
      .addCase(insertFeaturedStory.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('room_create_content_success');
      })
      .addCase(updateFeaturedStory.fulfilled, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = action.meta.arg.updateStoryDto.title
          ? translate('room_update_content_success')
          : translate('room_share_content_successful');
      })
      .addCase(insertFeaturedStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_story_error');
        }
      })
      .addCase(updateFeaturedStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_story_error');
        }
      })
      .addCase(deleteFeaturedStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('room_delete_content_error');
        }
      })
      .addCase(deleteFeaturedStory.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('room_delete_content_success');
      })
      .addCase(insertCommentOnStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_comment_on_story_error');
        }
      })
      .addCase(insertReplyToCommentOnStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_reply_on_story_error');
        }
      })
      .addCase(updateCommentOnStory.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_update_comment_success'
        );
      })
      .addCase(updateCommentOnStory.rejected, (state, action) => {
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = checkIfContentIsInappropriate(action)
            ? action.payload?.data?.['exception_message']
            : translate('insert_comment_on_story_error');
        }
      })
      .addCase(moderatorUpdateCommentOnStory.fulfilled, (state) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate(
          'snackbar_community_update_comment_success'
        );
      })
      .addCase(resendVerificationEmailLink.pending, (state) => {
        state.resendVerificationEmailLinkLoading = true;
      })
      .addCase(resendVerificationEmailLink.fulfilled, (state) => {
        state.resendVerificationEmailLinkCompleted = true;
        state.resendVerificationEmailLinkLoading = false;
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.success;
        state.snackbarMessage = translate('send_verification_link_succes');
      })
      .addCase(resendVerificationEmailLink.rejected, (state, action) => {
        state.resendVerificationEmailLinkLoading = false;
        if (checkIfMemberIsUnverified(action)) {
          state.openUnverifiedMemberDialog = true;
        } else {
          state.snackbarOpen = true;
          state.snackbarType = SnackbarType.error;
          state.snackbarMessage = translate('send_verification_link_error');
        }
      })
      .addCase(createPrivateRoom.rejected, (state, action) => {
        state.snackbarOpen = true;
        state.snackbarType = SnackbarType.error;
        if (
          action.payload?.data?.['exception_status'] ===
          HttpStatusCode.server_error
        ) {
          state.snackbarMessage = translate('login_error');
        } else {
          state.snackbarMessage = action.payload?.data;
        }
      })

      .addCase(uploadVideos.pending, (state) => {
        state.uploadingVideo = true;
        state.uploadVideoResult = {
          error: '',
          video: undefined,
          originalFilename: '',
        };
      })
      .addCase(uploadVideos.fulfilled, (state, action: any) => {
        state.uploadingVideo = false;
        state.uploadVideoResult = {
          error: '',
          video: {
            assetName: action.payload?.videoProcessing?.asset?.assetName,
          },
          originalFilename: action.payload.originalFilename,
        };
      })
      .addCase(uploadVideos.rejected, (state, payload) => {
        state.uploadingVideo = false;
        state.uploadVideoResult = {
          error: payload.error.message,
          video: undefined,
          originalFilename: '',
        };
      })

      .addDefaultCase((state, action) => {
        if (action.payload && action.payload.status === 403) {
          if (
            action.payload?.data &&
            action.payload?.data?.['error_сode'] === 'MEMBER_UNVERIFIED'
          ) {
            state.openUnverifiedMemberDialog = true;
          }
        }
      });
  },
});

export const {
  setOffline,
  showSnackbar,
  hideSnackbar,
  setLanguage,
  showBottomNavigation,
  hideBottomNavigation,
  setIsAuthenticated,
  setIsAuthInProgress,
  setOpenUnverifiedMemberDialog,
  setIsVideoUploading,
  deleteVideoResult,
  setShouldOpenImagePicker,
  setServerDirective,
  clearServerDirective,
} = appSlice.actions;

export default appSlice.reducer;
